import { Layers, Mail, Megaphone, Ticket, TrendingUp, Users, UtensilsCrossed } from 'lucide-react';
import { sectionCls } from '@/pages/launch/helpers';

interface LaunchSolutionSectionProps {
  sectionRef: React.Ref<HTMLDivElement>;
  visible: boolean;
}

const silos = [
  { icon: Ticket, label: 'Ticketing', sub: 'Eventbrite, DICE, AXS' },
  { icon: UtensilsCrossed, label: 'POS', sub: 'Toast, Square, Lightspeed' },
  { icon: Users, label: 'CRM', sub: 'Spreadsheets, SevenRooms' },
  { icon: Mail, label: 'Email & SMS', sub: 'Mailchimp, Klaviyo' },
  { icon: Megaphone, label: 'Social & Ads', sub: 'Meta, TikTok, Google' },
  { icon: TrendingUp, label: 'Market Intel', sub: 'Gut feel, word of mouth' },
];

const LaunchSolutionSection = ({ sectionRef, visible }: LaunchSolutionSectionProps) => (
  <section
    id="the-solution"
    ref={sectionRef}
    className={`py-16 sm:py-24 px-4 sm:px-6 ${sectionCls(visible)}`}
  >
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-12 sm:mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">
          The Solution
        </p>
        <h2 className="text-3xl sm:text-4xl font-display font-bold">
          Your Venue Runs on 6 Tools.
          <br className="hidden sm:block" />{' '}
          <span className="gradient-text">We Connect All of Them.</span>
        </h2>
        <p className="text-muted-foreground mt-4 max-w-2xl mx-auto text-base">
          Every venue we've talked to uses some combination of these. The problem isn't the tools —
          it's that none of them talk to each other.
        </p>
      </div>

      <div className="relative">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {silos.map((silo) => (
            <div
              key={silo.label}
              className="group relative flex flex-col items-center text-center p-4 sm:p-6 rounded-xl border border-border/60 bg-card hover:border-primary/40 hover:shadow-md transition-all duration-300"
            >
              <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl bg-primary/8 flex items-center justify-center mb-3 group-hover:bg-primary/15 transition-colors">
                <silo.icon size={20} className="text-primary sm:[&]:w-6 sm:[&]:h-6" />
              </div>
              <p className="font-semibold text-sm mb-0.5">{silo.label}</p>
              <p className="text-[11px] text-muted-foreground leading-snug">{silo.sub}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center mt-8 sm:mt-12">
        <div className="relative flex items-center gap-3 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border border-primary/25 rounded-2xl px-6 sm:px-8 py-4 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
            <Layers size={20} className="text-primary" />
          </div>
          <div className="text-left">
            <p className="text-sm sm:text-base font-semibold text-foreground">
              One intelligence layer connects all six.
            </p>
            <p className="text-xs text-muted-foreground">
              Stop switching tabs. Start seeing the full picture.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default LaunchSolutionSection;
